import { useEffect, useId, useRef, useState } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { validAddressResults } from '../lib/address-search';

interface AddressItem {
  tekst: string;
  data: { x: number; y: number };
}

interface Props {
  defaultValue?: string;
  value?: string;
  onChange?: (address: string) => void;
  placeholder?: string;
  name?: string;
  required?: boolean;
  onSelectAddress?: (address: string, center: [number, number]) => void;
  className?: string;
}

export default function AddressAutocomplete({
  defaultValue = '',
  value,
  onChange,
  placeholder = 'Søg adresse (f.eks. Svallerup Søvej 7, Kalundborg)',
  name = 'address',
  required = false,
  onSelectAddress,
  className = 'form-input',
}: Props) {
  const [localQuery, setQuery] = useState(defaultValue);
  const query = value ?? localQuery;
  const listId = useId();
  const [activeIndex, setActiveIndex] = useState(-1);
  const selectedQuery = useRef('');
  const sequence = useRef(0);
  const request = useRef<AbortController | null>(null);
  const [results, setResults] = useState<AddressItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    const current = ++sequence.current;
    const controller = new AbortController();
    request.current = controller;
    setResults([]);
    setOpen(false);
    setLoading(false);
    setActiveIndex(-1);
    const raw = query.trim();
    if (raw.length < 3 || raw === selectedQuery.current) return;
    const timer = window.setTimeout(async () => {
      if (controller.signal.aborted || current !== sequence.current) return;
      setLoading(true);
      try {
        const response = await fetch(`/api/address?q=${encodeURIComponent(raw)}`, {
          signal: controller.signal,
        });
        const payload = await response.json();
        if (!response.ok || current !== sequence.current || controller.signal.aborted) return;
        const valid = validAddressResults(payload.results);
        setResults(valid);
        setOpen(valid.length > 0);
      } catch {
        /* Ignore cancelled and unavailable address searches. */
      } finally {
        if (current === sequence.current && !controller.signal.aborted) setLoading(false);
      }
    }, 280);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  function selectAddress(item: AddressItem) {
    request.current?.abort();
    ++sequence.current;
    selectedQuery.current = item.tekst;
    setQuery(item.tekst);
    onChange?.(item.tekst);
    setOpen(false);
    setLoading(false);
    onSelectAddress?.(item.tekst, [item.data.x, item.data.y]);
  }

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e: PointerEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        request.current?.abort();
        ++sequence.current;
        setLoading(false);
        setOpen(false);
      }
    }
    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input
          className={className}
          name={name}
          required={required}
          value={query}
          placeholder={placeholder}
          autoComplete="off"
          role="combobox"
          aria-label="Adresse"
          aria-autocomplete="list"
          aria-expanded={open && results.length > 0}
          aria-controls={listId}
          aria-activedescendant={open && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              request.current?.abort();
              ++sequence.current;
              setOpen(false);
              setLoading(false);
            }
            if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && results.length) {
              e.preventDefault();
              setOpen(true);
              setActiveIndex((index) =>
                index < 0
                  ? e.key === 'ArrowDown'
                    ? 0
                    : results.length - 1
                  : (index + (e.key === 'ArrowDown' ? 1 : -1) + results.length) % results.length,
              );
            }
            if (e.key === 'Enter' && open && activeIndex >= 0 && results[activeIndex]) {
              e.preventDefault();
              selectAddress(results[activeIndex]);
            }
          }}
          style={{ width: '100%', paddingRight: '32px' }}
          onChange={(e) => {
            request.current?.abort();
            ++sequence.current;
            setResults([]);
            setActiveIndex(-1);
            selectedQuery.current = '';
            setQuery(e.target.value);
            onChange?.(e.target.value);
            setOpen(false);
          }}
          onFocus={() => {
            if (results.length > 0) setOpen(true);
          }}
        />
        <div
          style={{
            position: 'absolute',
            right: 10,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            color: '#809568',
          }}
        >
          {loading ? <Loader2 size={16} className="spinning" /> : <MapPin size={16} />}
        </div>
      </div>

      {open && results.length > 0 && (
        <ul
          id={listId}
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 'calc(var(--z-sticky) + 20)',
            background: '#ffffff',
            borderRadius: 6,
            boxShadow: '0 8px 24px rgba(24, 42, 48, 0.18)',
            border: '1px solid #d9e2d1',
            maxHeight: 220,
            overflowY: 'auto',
            margin: 0,
            padding: '4px 0',
            listStyle: 'none',
          }}
        >
          {results.map((item, idx) => (
            <li
              key={idx}
              id={`${listId}-${idx}`}
              role="option"
              aria-selected={activeIndex === idx}
              style={{ margin: 0, padding: 0 }}
            >
              <button
                type="button"
                tabIndex={-1}
                onMouseDown={(e) => e.preventDefault()}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '9px 12px',
                  border: 'none',
                  background: activeIndex === idx ? '#f2f6ec' : 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: '#263f2b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  lineHeight: 1.4,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f2f6ec')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                onClick={() => selectAddress(item)}
              >
                <MapPin size={13} style={{ flexShrink: 0, color: '#4a6b40' }} />
                <span>{item.tekst}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
